/**
 * SendLetter — iter303ak-fix67 · F1 (P0 · reviewer-informed)
 *
 * Purpose: Real behaviour for the `send_letter` primitive (6 usages).
 * Previously fell through to GenericFallback, so the operator's "Send"
 * click was silently discarded — no letter dispatched, no audit log.
 *
 * Behaviour: POSTs to `/api/workflow/side-effects/send-letter` which
 * (currently MOCKED) records the intent in a `letter_dispatches` collection
 * with `channel`, `letter_key`, `appraisal_id`, `sent_at`, and `mock: true`.
 * When SMTP creds land (backlog), the mock flag flips. Frontend advances
 * on 200 and shows the dispatch id + timestamp.
 *
 * Params:
 *   letter_key   · required — which letter template (e.g. "appraisal_authorisation")
 *   channel      · "email" (default) | "sms" | "post"
 *   header_title · card header
 *   label        · button label · default "Send now"
 *   subject      · optional — override the letter's default subject line
 */
import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { selectAcceptedLetterKey } from './_letterSelector';

// Local axios instance mirroring the pattern used by sibling renderers.
const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function SendLetter({ action, value, onChange, onNext, onBack, canBack, capturedData }) {
  const params = action.params || {};
  // `letter_selector` picks the right drafted letter from run data (e.g. Accepted: deposit received / not needed / awaiting).
  const letterKey   = (params.letter_selector && selectAcceptedLetterKey(params.letter_selector, capturedData, null))
    || params.letter_key || params.template_key || '';
  const channel     = (params.channel || 'email').toLowerCase();
  const label       = params.label || 'Send now';
  const headerTitle = params.header_title || `Send ${channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'Letter'}`;
  const subject     = params.subject || '';

  const appraisalId = capturedData?.appraisal_id || capturedData?.['appraisal.id'] || '';
  const [sending, setSending] = useState(false);
  const [dispatch, setDispatch] = useState(value?.dispatch_id ? value : null);
  const [err, setErr] = useState('');

  const doSend = async () => {
    setErr(''); setSending(true);
    try {
      const { data } = await api.post('/workflow/side-effects/send-letter', {
        letter_key:   letterKey,
        channel,
        appraisal_id: appraisalId,
        subject,
      });
      const payload = {
        dispatch_id: data?.dispatch_id,
        sent_at:     data?.sent_at,
        mock:        !!data?.mock,
        channel,
        letter_key:  letterKey,
      };
      setDispatch(payload);
      onChange(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm space-y-4"
         data-testid="wf-send-letter">
      <div className="flex items-center gap-2 text-slate-800">
        <Send className="w-5 h-5" />
        <h3 className="font-semibold">{headerTitle}</h3>
      </div>

      <div className="text-xs text-slate-600 space-y-0.5">
        <div><span className="font-medium">Letter:</span> {letterKey || <span className="text-rose-600">missing letter_key param</span>}</div>
        <div><span className="font-medium">Channel:</span> {channel}</div>
        {subject && <div><span className="font-medium">Subject:</span> {subject}</div>}
      </div>

      {!dispatch && (
        <button
          onClick={doSend}
          disabled={!letterKey || sending}
          data-testid="wf-send-letter-btn"
          className="w-full px-6 py-4 border-2 border-indigo-500 rounded font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40"
        >
          {sending ? 'Sending…' : label}
        </button>
      )}

      {dispatch && (
        <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 flex items-start gap-2"
             data-testid="wf-send-letter-done">
          <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-600 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-slate-800">Sent · dispatch {dispatch.dispatch_id?.slice(-8)}</div>
            <div className="text-xs text-slate-600">{new Date(dispatch.sent_at).toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' })}</div>
            {dispatch.mock && (
              <div className="text-[10px] uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 inline-block mt-1 font-bold">
                Email sends for real; SMS and post are recorded only
              </div>
            )}
          </div>
        </div>
      )}

      {err && (
        <div className="p-2 rounded border border-rose-200 bg-rose-50 text-xs text-rose-700 flex items-start gap-2"
             data-testid="wf-send-letter-err">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          {err}
        </div>
      )}

      <div className="flex justify-between gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-send-letter-back"
            className="px-4 py-2 rounded text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">
            Back
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!dispatch}
          data-testid="wf-send-letter-next"
          className="ml-auto px-6 py-2 rounded font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
